import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  LinePosition,
  TabsContextProps,
  TabsItemProps,
  TabsProps,
} from "./Tabs.types";
import styles from "./Tabs.module.scss";
import { Typography } from "../index";
import { CIMoreHorizontal } from "@dua.design/icons";

const TabsContext = createContext<TabsContextProps>(null);

const Tabs: FC<TabsProps> & { Item: FC<TabsItemProps> } = ({
  children,
  items,
  disableAnimation,
}: TabsProps) => {
  const [linePosition, setLinePosition] = useState<LinePosition>({});
  const tabsRef = useRef<HTMLDivElement>(null);
  const [overflowedTabs, setOverflowedTabs] = useState<TabsItemProps[]>([]);
  const moveLine = (line: LinePosition) => {
    setLinePosition(line);
  };

  const [resize, setResize] = useState(0);

  useEffect(() => {
    if (!items) {
      return;
    }
    const { current } = tabsRef;
    let nonOverflow = 0;
    if (current) {
      for (let i = 0; i < current.children.length; i++) {
        if (
          current.children[i].getBoundingClientRect().top ===
          current.getBoundingClientRect().top
        ) {
          nonOverflow++;
        }
      }
    }
    setOverflowedTabs(items.slice(nonOverflow));
  }, [tabsRef, resize, items]);

  useEffect(() => {
    const onResize = () => {
      setResize((prev) => prev + 1);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [tabsRef]);

  return (
    <div className={styles.root}>
      <div className={styles.Tabs} data-testid="Tabs" ref={tabsRef}>
        <TabsContext.Provider value={{ moveLine }}>
          {items
            ? items.map((item, index) => {
                return (
                  <Tabs.Item key={index} {...item}>
                    {item.children}
                  </Tabs.Item>
                );
              })
            : children}
        </TabsContext.Provider>
        <div
          className={[
            styles.line,
            disableAnimation && styles.disableAnimation,
          ].join(" ")}
          style={linePosition}
        />
      </div>
      <div className={styles.MenuContainer}>
        {overflowedTabs.length !== 0 && (
          <button className={styles.ellipsis}>
            <CIMoreHorizontal size={"18px"} />
          </button>
        )}
        <div className={styles.menu}>
          {overflowedTabs.map((item, index) => {
            return (
              <button
                key={index}
                className={[
                  styles.menuItem,
                  item.selected && styles.activeMenuItem,
                ].join(" ")}
                {...item}
              >
                <Typography variant={"span"} weight={"medium"} token={100}>
                  {item.children}
                </Typography>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Item: FC<TabsItemProps> = ({ children, selected, ...rest }) => {
  const { moveLine } = useContext(TabsContext);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (selected) {
      // if ref is in the same line as the parent
      if (
        ref.current.getBoundingClientRect().top !==
        ref.current.parentElement.getBoundingClientRect().top
      ) {
        moveLine({
          left: 0,
          width: 0,
        });
        return;
      }
      moveLine({
        left: ref.current?.offsetLeft,
        width: ref.current?.offsetWidth,
      });
    }
  }, [selected]);

  return (
    <button
      {...rest}
      className={[styles.TabsItem, rest.className].join(" ")}
      data-testid="TabsItem"
      ref={ref}
    >
      <Typography
        variant={"p"}
        token={100}
        weight={"medium"}
        className={selected ? styles.selectedTab : styles.notSelectedTab}
      >
        {children}
      </Typography>
    </button>
  );
};

Tabs.Item = Item;

export default Tabs;
